import {
  ChatContainerTypes,
  ChatCustomBlockTypes,
  type BlockButtons,
  type ChatContainers,
  type ChatCustomBlock,
  type ChatCustomContainers,
} from "@do/shared-types";

export type CombinedBlock = ChatCustomBlock;
export type CombinedContainer = {
  id: string;
  type: "unknown" | ChatCustomBlockTypes | ChatContainerTypes;
  blocks: Array<CombinedBlock | CombinedContainer>;
};

export type CombinedItem = CombinedBlock | CombinedContainer;

function omitContainerFields<T extends Record<string, unknown>>(obj: T): T {
  const copy: Record<string, unknown> = { ...obj };
  delete copy["containerCuid"];
  delete copy["container_cuid"];
  return copy as T;
}

function getBlockContainerCuid(blockLike: unknown): string | null {
  if (!blockLike || typeof blockLike !== "object") return null;
  const anyBlock = blockLike as Record<string, unknown>;

  const camel = anyBlock["containerCuid"];
  if (typeof camel === "string" && camel.length > 0) return camel;

  const snake = anyBlock["container_cuid"];
  if (typeof snake === "string" && snake.length > 0) return snake;

  return null;
}

function getContainerParentCuid(containerLike: unknown): string | null {
  if (!containerLike || typeof containerLike !== "object") return null;
  const anyContainer = containerLike as Record<string, unknown>;

  const camel = anyContainer["parentContainerCuid"];
  if (typeof camel === "string" && camel.length > 0) return camel;

  const snake = anyContainer["parent_container_cuid"];
  if (typeof snake === "string" && snake.length > 0) return snake;

  return null;
}

export function combineBlocksAndContainers({
  blocks,
  containers,
}: {
  blocks: ChatCustomBlock[];
  containers: ChatContainers | ChatCustomContainers;
}): CombinedItem[] {
  const containerByCuid = new Map(containers.map((c) => [c.cuid, c]));

  const resolveNonSectionContainerCuid = (cuid: string | null): string | null => {
    let current = cuid;
    while (current) {
      const container = containerByCuid.get(current);
      if (!container) return current;
      if (container.type === ChatContainerTypes.section) {
        current = getContainerParentCuid(container);
        continue;
      }
      return current;
    }
    return null;
  };

  const resolveNonSectionParentCuid = (cuid: string): string | null => {
    const container = containerByCuid.get(cuid);
    let currentParent = getContainerParentCuid(container);

    while (currentParent) {
      const parent = containerByCuid.get(currentParent);
      if (!parent) return currentParent;
      if (parent.type === ChatContainerTypes.section) {
        currentParent = getContainerParentCuid(parent);
        continue;
      }
      return currentParent;
    }

    return null;
  };

  const root: CombinedItem[] = [];
  const containerEntryByCuid = new Map<string, CombinedContainer>();

  const ensureContainerEntry = (rawCuid: string): CombinedContainer => {
    const cuid = resolveNonSectionContainerCuid(rawCuid) ?? rawCuid;

    const existing = containerEntryByCuid.get(cuid);
    if (existing) return existing;

    const container = containerByCuid.get(cuid);
    const entry: CombinedContainer = container
      ? ({ ...container, id: cuid, blocks: [] } as CombinedContainer)
      : ({ id: cuid, cuid, type: "unknown", blocks: [] } as CombinedContainer);
    containerEntryByCuid.set(cuid, entry);

    const parentCuid = resolveNonSectionParentCuid(cuid);
    if (parentCuid) {
      const parentEntry = ensureContainerEntry(parentCuid);
      parentEntry.blocks.push(entry);
    } else {
      root.push(entry);
    }

    return entry;
  };

  const pushLeaf = (leaf: CombinedBlock, containerCuid: string | null) => {
    const target = resolveNonSectionContainerCuid(containerCuid);
    if (!target) {
      root.push(leaf);
      return;
    }

    const containerEntry = ensureContainerEntry(target);
    containerEntry.blocks.push(leaf);
  };

  for (const block of blocks) {
    if (block.type === ChatCustomBlockTypes.buttons && "buttons" in block) {
      // Keep the block "buttons" intact.
      // If the block itself has no container, but its buttons do, we attach the block to that container
      // (or split it by container if buttons reference multiple containers).
      const blockContainerCuid = getBlockContainerCuid(block);
      if (blockContainerCuid) {
        pushLeaf(omitContainerFields(block), blockContainerCuid);
        continue;
      }

      const byContainer = new Map<string, BlockButtons["buttons"]>();
      for (const btn of block.buttons ?? []) {
        const resolved = resolveNonSectionContainerCuid(getBlockContainerCuid(btn));
        if (!resolved) continue;
        const group = byContainer.get(resolved) ?? [];
        group.push(
          omitContainerFields(
            btn as unknown as Record<string, unknown>
          ) as BlockButtons["buttons"][number]
        );
        byContainer.set(resolved, group);
      }

      // No container info anywhere → treat as root-level buttons block.
      if (byContainer.size === 0) {
        pushLeaf(omitContainerFields(block), null);
        continue;
      }

      // Single container → keep one buttons block.
      if (byContainer.size === 1) {
        const [onlyCuid, onlyButtons] = Array.from(byContainer.entries())[0];
        pushLeaf(
          { ...omitContainerFields(block), buttons: onlyButtons } as ChatCustomBlock,
          onlyCuid
        );
        continue;
      }

      // Multiple containers → split into multiple buttons blocks (still keeping type:"buttons").
      for (const [cuid, buttonsForCuid] of byContainer.entries()) {
        pushLeaf(
          { ...omitContainerFields(block), buttons: buttonsForCuid } as unknown as ChatCustomBlock,
          cuid
        );
      }
      continue;
    }

    pushLeaf(omitContainerFields(block), getBlockContainerCuid(block));
  }

  return root;
}

export const isCombinedContainer = (item: CombinedItem): item is CombinedContainer =>
  Boolean(item) &&
  typeof item === "object" &&
  "blocks" in item &&
  Array.isArray((item as { blocks?: unknown }).blocks);

export const isCombinedBlock = (item: CombinedItem): item is CombinedBlock =>
  !isCombinedContainer(item);

export function getBlockIdentifier(block: CombinedItem, index: number): string {
  if (isCombinedContainer(block)) return block.id;
  if ("id" in block && typeof block.id === "string" && block.id.length > 0) return block.id;
  if ("cuid" in block && typeof block.cuid === "string" && block.cuid.length > 0) return block.cuid;
  if ("html_id" in block && typeof block.html_id === "string" && block.html_id.length > 0)
    return block.html_id;
  return `${block.type}-${index}`;
}
